import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import SmsAnalysisAPI from "../../services/SmsAnalysisAPI";
import ScamReportAPI from "../../services/ScamReportAPI";
import { useUser } from "../../context/UserContext";
import ReadOnlyBanner from "../../common/ReadOnlyBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faSms,
} from "@fortawesome/free-solid-svg-icons";
import "../../css/Common.css";
import "./css/TextMessageAnalysis.css";
import "./css/SmsAnalysisSimple.css";

// Import desktop and mobile specific components
import SmsAnalysisSimpleDesktop from "./SmsAnalysisSimpleDesktop";
import SmsAnalysisSimpleMobile from "./SmsAnalysisSimpleMobile";

const SmsAnalysisSimple = ({
  onToggleView,
  targetAnalysisId,
  onTargetSelected,
  setHeroContent,
}) => {
  const { loggedInUser } = useUser();
  const userUuid = loggedInUser?.uuid || loggedInUser?.id;
  const { t } = useTranslation();

  // Mobile detection constant
  const isMobile = () => window.innerWidth <= 768;

  // Core state
  const [analyses, setAnalyses] = useState([]);
  const [error, setError] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [reportIdMap, setReportIdMap] = useState({});
  const loadAllAnalysesRef = useRef(null);
  const checkQuotaStatusRef = useRef(null);
  const loadAnalysisForNotificationRef = useRef(null);
  // "idle" | "waiting" | "timedOut" — set after the user says they forwarded a text
  const [awaitState, setAwaitState] = useState("idle");
  const awaitBaselineRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Mobile original message state - true when original message is expanded in mobile
  const [showMobileOriginalMessage, setShowMobileOriginalMessage] =
    useState(false);

  // Original message inline display state
  const [showOriginalMessageInline, setShowOriginalMessageInline] =
    useState(false);

  // Mobile tab state - for tab navigation on mobile
  const [activeTab, setActiveTab] = useState("results"); // results, details

  // Mobile initial view - determine if we should show tabs or desktop layout
  const [showMobileTabView, setShowMobileTabView] = useState(isMobile());

  // Load all analyses on mount
  useEffect(() => {
    loadAllAnalysesRef.current?.();
    checkQuotaStatusRef.current?.();
  }, [userUuid]);

  // Poll for the forwarded text so the user doesn't have to keep pressing Refresh
  useEffect(() => {
    if (awaitState !== "waiting") return undefined;
    loadAllAnalysesRef.current?.();
    // Skip polls while the tab is hidden; the overall timeout still applies.
    const poll = setInterval(() => {
      if (!document.hidden) loadAllAnalysesRef.current?.();
    }, 10000);
    const timeout = setTimeout(() => setAwaitState("timedOut"), 180000);
    return () => {
      clearInterval(poll);
      clearTimeout(timeout);
    };
  }, [awaitState]);

  // Open the newly arrived analysis automatically
  useEffect(() => {
    if (awaitState !== "waiting" || !awaitBaselineRef.current) return;
    const arrived = analyses.find((a) => !awaitBaselineRef.current.has(a.id));
    if (!arrived) return;
    setAwaitState("idle");
    setSelectedAnalysis(arrived);
    if (window.innerWidth <= 768) setActiveTab("details");
  }, [analyses, awaitState]);

  const startAwaitingForward = () => {
    awaitBaselineRef.current = new Set(analyses.map((a) => a.id));
    setAwaitState("waiting");
  };

  const cancelAwaitingForward = () => {
    awaitBaselineRef.current = null;
    setAwaitState("idle");
  };

  // Check if quota was previously exceeded today
  const checkQuotaStatus = () => {
    if (!userUuid) return;
    const quotaKey = `sms_quota_exceeded_${userUuid}_${new Date().toDateString()}`;
    const exceeded = localStorage.getItem(quotaKey) === "true";
    setQuotaExceeded(exceeded);
  };
  checkQuotaStatusRef.current = checkQuotaStatus;

  // Handle window resize to update mobile view state
  useEffect(() => {
    const handleResize = () => {
      const mobileView = isMobile();
      setShowMobileTabView(mobileView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-select analysis if targetAnalysisId is provided (for notification navigation)
  useEffect(() => {
    if (targetAnalysisId && analyses.length > 0) {
      console.log(
        "SmsAnalysisSimple: Looking for target analysis with ID:",
        targetAnalysisId,
      );
      console.log("SmsAnalysisSimple: Available analyses:", analyses.length);

      const targetAnalysis = analyses.find(
        (analysis) => analysis.id === parseInt(targetAnalysisId),
      );

      if (targetAnalysis) {
        console.log(
          "SmsAnalysisSimple: Found target analysis, auto-selecting:",
          targetAnalysis,
        );
        setSelectedAnalysis(targetAnalysis);

        if (onTargetSelected) {
          onTargetSelected();
        }

        // Enhanced navigation to details
        if (isMobile()) {
          console.log(
            "SmsAnalysisSimple: Setting details tab for notification navigation",
          );
          setActiveTab("details");
        } else {
          console.log(
            "SmsAnalysisSimple: Desktop mode - scrolling to details pane",
          );
          const scrollAttempts = [100, 300, 500, 800];
          scrollAttempts.forEach((delay, index) => {
            setTimeout(() => {
              scrollToAnalysisDetails();
              console.log(
                `SmsAnalysisSimple: Notification navigation scroll attempt ${
                  index + 1
                } - Scrolled to details`,
              );
            }, delay);
          });
        }
      } else {
        console.log(
          "SmsAnalysisSimple: Target analysis not found in current results",
        );
        loadAnalysisForNotificationRef.current?.(targetAnalysisId);
      }
    }
  }, [targetAnalysisId, analyses, onTargetSelected]);

  // Load specific analysis for notification if not found in recent results
  const loadAnalysisForNotification = async (analysisId) => {
    try {
      console.log(
        "SmsAnalysisSimple: Loading specific analysis for notification:",
        analysisId,
      );
      const result = await SmsAnalysisAPI.getSmsAnalysisById(analysisId);

      if (result) {
        console.log("SmsAnalysisSimple: Found target analysis:", result);

        const transformed = transformAnalysisData([result]);
        setAnalyses((prevAnalyses) => {
          const existing = prevAnalyses.find((a) => a.id === result.id);
          if (!existing) {
            return [transformed[0], ...prevAnalyses];
          }
          return prevAnalyses;
        });
        setSelectedAnalysis(transformed[0]);

        // Show details view
        if (isMobile()) {
          setActiveTab("details");
        } else {
          const scrollAttempts = [200, 500, 800];
          scrollAttempts.forEach((delay, index) => {
            setTimeout(() => {
              scrollToAnalysisDetails();
              console.log(
                `SmsAnalysisSimple: Notification scroll attempt ${
                  index + 1
                } - Scrolled to details`,
              );
            }, delay);
          });
        }
      } else {
        console.warn("SmsAnalysisSimple: Target analysis not found");
        setError(t("sms.error.notFound"));
      }

      if (onTargetSelected) {
        onTargetSelected();
      }
    } catch (error) {
      console.error(
        "SmsAnalysisSimple: Error loading analysis for notification:",
        error,
      );
      setError(t("sms.error.loadNotifFailed"));
      if (onTargetSelected) {
        onTargetSelected();
      }
    }
  };
  loadAnalysisForNotificationRef.current = loadAnalysisForNotification;

  // Core data loading function - Get latest results from last 30 days, limited to 20
  const loadAllAnalyses = async () => {
    if (!userUuid) {
      setError(t("sms.error.notAuthenticated"));
      return;
    }

    try {
      setError(null);

      console.log(
        "SmsAnalysisSimple: Loading all analyses for user:",
        userUuid,
      );
      const results = await SmsAnalysisAPI.searchSmsAnalyses({ userUuid });
      console.log("SmsAnalysisSimple: Received results from API:", results);
      console.log("SmsAnalysisSimple: Results length:", results?.length);

      // Transform the raw data to match the format expected by components
      const transformedResults = transformAnalysisData(results || []);
      console.log(
        "SmsAnalysisSimple: Transformed results:",
        transformedResults,
      );

      setAnalyses(transformedResults);

      // Look up reportIds for scam-detected history items
      const scamItems = transformedResults.filter(
        (item) => item.analysis?.analysisResult?.isScam === true && item.id,
      );
      if (scamItems.length > 0) {
        try {
          const sourceIds = scamItems.map((item) => item.id);
          const lookupResult = await ScamReportAPI.lookupReportIdsBySource(
            "SMS_ANALYSIS",
            sourceIds,
          );
          const existingMap = lookupResult?.mapping || {};
          setReportIdMap(existingMap);

          // Auto-save reports for scam items that don't have one yet
          const unsavedItems = scamItems.filter(
            (item) => !existingMap[item.id],
          );
          if (unsavedItems.length > 0) {
            const additionalMap = {};
            for (const item of unsavedItems) {
              try {
                const result = item.analysis.analysisResult;
                const saveResponse = await ScamReportAPI.saveReport({
                  analysisType: "SMS_ANALYSIS",
                  analysisTitle: "SMS Scam Detected",
                  analysisDetail:
                    "Forwarded SMS analysis detected suspicious content",
                  analysisInput: `From: ${item.senderPhone || "Unknown"}\n${item.messageContent || ""}`,
                  isScam: true,
                  confidence: result.confidence || 0,
                  riskLevel: result.riskLevel || "HIGH",
                  explanation: result.reasoning || "",
                  suggestions: result.suggestions || "",
                  sourceAnalysisId: item.id,
                  sourceAnalysisType: "SMS_ANALYSIS",
                });
                if (saveResponse.reportId) {
                  additionalMap[item.id] = saveResponse.reportId;
                }
              } catch (saveErr) {
                console.error(
                  "[SmsAnalysisSimple] Failed to auto-save report:",
                  item.id,
                  saveErr,
                );
              }
            }
            if (Object.keys(additionalMap).length > 0) {
              setReportIdMap((prev) => ({ ...prev, ...additionalMap }));
            }
          }
        } catch (lookupErr) {
          console.error("Failed to look up SMS report IDs:", lookupErr);
        }
      }
    } catch (err) {
      console.error("Error loading SMS analyses:", err);

      // Check if quota exceeded (429 status)
      if (err.response?.status === 429) {
        const errorMessage = err.response?.data?.error || "";
        if (errorMessage.includes("Daily SMS analysis limit exceeded")) {
          const quotaKey = `sms_quota_exceeded_${userUuid}_${new Date().toDateString()}`;
          localStorage.setItem(quotaKey, "true");
          setQuotaExceeded(true);
          return; // Don't show generic error
        }
      }

      setError(t("sms.error.loadFailed"));
    } finally {
      setHasLoaded(true);
    }
  };
  loadAllAnalysesRef.current = loadAllAnalyses;

  // Helper function to transform raw analysis data
  const transformAnalysisData = (analysisData) => {
    return analysisData.map((item) => {
      return {
        id: item.id,
        messageSid: item.messageSid,
        lastModified: parseDateTime(item.analyzedAt),
        senderPhone: item.senderPhone || item.forwarderPhone || "Unknown",
        forwarderPhone: item.forwarderPhone,
        messageContent: item.messageContent || "No message content",
        userContext: item.userContext || "",
        reportId: item.reportId || null,
        analysis: {
          analysisResult: {
            isScam: item.isScam || false,
            confidence: item.confidence || 0,
            scamType: getThreatTypeDisplay(item.scamType, item.confidence || 0),
            riskLevel: item.riskLevel || "UNKNOWN",
            riskScore: item.confidence || 0,
            reasoning: item.analysisExplanation || "",
            suspiciousElements: parseRiskFactors(item.riskFactors),
            suggestions: item.suggestions || "",
            hasSuspiciousLinks: item.hasSuspiciousLinks || false,
            hasUrgentLanguage: item.hasUrgentLanguage || false,
            hasFinancialThreats: item.hasFinancialThreats || false,
          },
          analysisTimestamp: parseDateTime(item.analyzedAt),
          analysisMethod: "BACKEND_API",
        },
      };
    });
  };

  // Helper function to determine threat type display based on confidence
  const getThreatTypeDisplay = (scamType, confidence) => {
    if (confidence <= 0.2) {
      return "None";
    }
    return scamType || "UNKNOWN";
  };

  // Helper function to parse risk factors
  const parseRiskFactors = (riskFactors) => {
    // If already an array, return it directly
    if (Array.isArray(riskFactors)) {
      return riskFactors;
    }

    // If it's a string, try to parse it
    if (typeof riskFactors === "string") {
      try {
        const parsed = JSON.parse(riskFactors);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        // If parsing fails, try to split by common delimiters
        return riskFactors
          .split(/[,;|\n]/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      }
    }

    // If it's neither array nor string, return empty array
    return [];
  };

  // Helper function to parse datetime
  const parseDateTime = (timeString) => {
    if (!timeString) return new Date();

    try {
      let parsedTime;

      if (typeof timeString === "string") {
        // Add 'Z' if it's missing for UTC parsing
        if (
          timeString.includes("T") &&
          !timeString.includes("Z") &&
          !timeString.includes("+")
        ) {
          timeString = timeString + "Z";
        }
        parsedTime = new Date(timeString);
      } else if (timeString instanceof Date) {
        parsedTime = timeString;
      } else {
        parsedTime = new Date(timeString);
      }

      // Check if the parsed time is valid
      if (isNaN(parsedTime.getTime())) {
        console.warn("Invalid time string:", timeString);
        return new Date();
      }

      return parsedTime;
    } catch (error) {
      console.warn("Error parsing time string:", timeString, error);
      return new Date();
    }
  };

  const handleAnalysisSelected = (analysis) => {
    setSelectedAnalysis(analysis);
    // Clear the original message inline display when selecting a new analysis
    setShowOriginalMessageInline(false);

    // Check if we're on mobile and switch to details tab
    if (isMobile()) {
      console.log(
        "SmsAnalysisSimple: Mobile detected, switching to details tab",
      );
      // Collapse original message when showing details in mobile
      setShowMobileOriginalMessage(false);
      setActiveTab("details"); // Switch to details tab instead of full-screen view
    } else {
      // For desktop mode, always scroll to details section with multiple attempts
      const scrollAttempts = [50, 150, 300];
      scrollAttempts.forEach((delay, index) => {
        setTimeout(() => {
          scrollToAnalysisDetails();
          console.log(
            `SmsAnalysisSimple: Manual selection scroll attempt ${
              index + 1
            } - Scrolled to details`,
          );
        }, delay);
      });
    }
  };

  // Navigation functions
  const scrollToAnalysisResults = () => {
    const element = document.querySelector(".results-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToAnalysisDetails = () => {
    const element = document.querySelector(".details-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      console.log(
        "SmsAnalysisSimple: Successfully scrolled to details section",
      );
    } else {
      // Primary selector not found, try fallback selectors
      const fallbackSelectors = [
        ".analysis-details",
        "[class*='details']",
        ".analysis-details-simple",
        "[class*='analysis-details']",
      ];

      for (const selector of fallbackSelectors) {
        const fallbackElement = document.querySelector(selector);
        if (fallbackElement) {
          fallbackElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          console.log(
            `SmsAnalysisSimple: Fallback scroll successful using selector: ${selector}`,
          );
          break;
        }
      }
    }
  };

  // Refresh function
  const refreshAnalyses = () => {
    loadAllAnalyses();
  };

  const onNavigateToManual = () => {
    if (setHeroContent) setHeroContent("Manual Input");
  };

  const removeAnalysis = (id) => {
    setAnalyses((prev) => prev.filter((item) => item.id !== id));
  };

  const handleReanalyzeWithContext = async (analysis, userContext) => {
    if (!analysis?.id) {
      throw new Error("No SMS analysis selected for re-analysis.");
    }

    const updated = await SmsAnalysisAPI.reanalyzeSmsAnalysis(
      analysis.id,
      userContext,
    );
    const transformed = transformAnalysisData([updated])[0];

    setAnalyses((prev) =>
      prev.map((item) => (item.id === transformed.id ? transformed : item)),
    );
    setSelectedAnalysis(transformed);

    // Update reportIdMap - set if exists, remove if null
    setReportIdMap((prev) => {
      const newMap = { ...prev };
      if (transformed?.reportId) {
        newMap[transformed.id] = transformed.reportId;
      } else {
        // Remove from map if reportId is null (scam cleared)
        delete newMap[transformed.id];
      }
      return newMap;
    });

    return transformed;
  };

  if (error) {
    return (
      <div className="common-container">
        <div className="common-section">
          <div className="common-section-content">
            <div className="error-message">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="icon-right-margin"
              />
              {error}
              <div className="error-actions">
                <button
                  onClick={refreshAnalyses}
                  className="common-button action-button"
                >
                  <FontAwesomeIcon icon={faSms} className="icon-right-margin" />
                  {t("sms.error.tryAgain")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`simple-view common-container${showMobileTabView ? " sasm-mobile-active" : ""}`}
    >
      <ReadOnlyBanner />
      {quotaExceeded && (
        <div
          className="quota-exceeded-banner"
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            style={{ color: "#856404", fontSize: "20px" }}
          />
          <div style={{ flex: 1 }}>
            <strong style={{ color: "#856404" }}>{t("sms.quota.title")}</strong>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#856404",
                fontSize: "14px",
              }}
            >
              {t("sms.quota.body")}
            </p>
          </div>
        </div>
      )}
      {showMobileTabView ? (
        <SmsAnalysisSimpleMobile
          selectedAnalysis={selectedAnalysis}
          analyses={analyses}
          handleAnalysisSelected={handleAnalysisSelected}
          handleReanalyzeWithContext={handleReanalyzeWithContext}
          refreshAnalyses={refreshAnalyses}
          userUuid={userUuid}
          setError={setError}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onAnalysisDeleted={removeAnalysis}
          showMobileOriginalMessage={showMobileOriginalMessage}
          setShowMobileOriginalMessage={setShowMobileOriginalMessage}
          showOriginalMessageInline={showOriginalMessageInline}
          setShowOriginalMessageInline={setShowOriginalMessageInline}
          reportIdMap={reportIdMap}
          onNavigateToManual={onNavigateToManual}
          userPhone={loggedInUser?.phone}
          awaitState={awaitState}
          onStartAwaiting={startAwaitingForward}
          onCancelAwaiting={cancelAwaitingForward}
          hasLoaded={hasLoaded}
        />
      ) : (
        <SmsAnalysisSimpleDesktop
          selectedAnalysis={selectedAnalysis}
          analyses={analyses}
          handleAnalysisSelected={handleAnalysisSelected}
          handleReanalyzeWithContext={handleReanalyzeWithContext}
          refreshAnalyses={refreshAnalyses}
          userUuid={userUuid}
          setError={setError}
          scrollToAnalysisResults={scrollToAnalysisResults}
          onAnalysisDeleted={removeAnalysis}
          showOriginalMessageInline={showOriginalMessageInline}
          setShowOriginalMessageInline={setShowOriginalMessageInline}
          showMobileOriginalMessage={showMobileOriginalMessage}
          setShowMobileOriginalMessage={setShowMobileOriginalMessage}
          reportIdMap={reportIdMap}
          onNavigateToManual={onNavigateToManual}
          userPhone={loggedInUser?.phone}
          awaitState={awaitState}
          onStartAwaiting={startAwaitingForward}
          onCancelAwaiting={cancelAwaitingForward}
          hasLoaded={hasLoaded}
        />
      )}
    </div>
  );
};

export default SmsAnalysisSimple;
